import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { authApi } from '../../services/authApi';
import {
	ForgotPasswordRequestSchema,
	OTP_EXPIRES_MINUTES,
	OTP_RESEND_COOLDOWN_SECONDS,
	isEmailIdentifier,
} from '../../schemas';
import { getFieldError, validateForm } from '../../schemas/forms';
import { describeMailError } from '../../lib/errorCodes';
import { Card, SignInContainer } from './LoginPage.styles';

type Notice = { severity: 'error' | 'warning'; text: string };

/** The email is carried only when one was typed: a username says nothing about where the code went. */
const resetHref = (identifier: string): string =>
	isEmailIdentifier(identifier)
		? `/reset-password?email=${encodeURIComponent(identifier.trim())}`
		: '/reset-password';

export default function ForgotPasswordPage() {
	const [identifier, setIdentifier] = React.useState('');
	const [validationErrors, setValidationErrors] = React.useState<z.ZodError | null>(null);
	const [notice, setNotice] = React.useState<Notice | null>(null);
	// The sent screen unmounts the form, so the identifier that was actually submitted is kept here.
	const [sentTo, setSentTo] = React.useState<string | null>(null);
	const [cooldown, setCooldown] = React.useState(0);

	React.useEffect(() => {
		if (cooldown <= 0) return;
		const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [cooldown]);

	const forgotMutation = useMutation({
		mutationFn: (value: string) => authApi.forgotPassword(value),
		onMutate: () => setNotice(null),
		onSuccess: (result, value) => {
			if (!result.exists) {
				setNotice({ severity: 'error', text: 'Không tìm thấy tài khoản.' });
				return;
			}
			if (result.noEmail) {
				setNotice({
					severity: 'warning',
					text: 'Tài khoản chưa có email nên không thể nhận mã. Vui lòng liên hệ quản trị viên.',
				});
				return;
			}
			setSentTo(value);
			setCooldown(OTP_RESEND_COOLDOWN_SECONDS);
		},
		onError: (error) => {
			setNotice({ severity: 'error', text: describeMailError(error, 'Không gửi được yêu cầu.') });
		},
	});

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validation = validateForm(ForgotPasswordRequestSchema, { identifier });
		if (!validation.success || !validation.data) {
			setValidationErrors(validation.errors ?? null);
			return;
		}
		setValidationErrors(null);
		forgotMutation.mutate(validation.data.identifier);
	};

	const handleResend = () => {
		if (sentTo && cooldown <= 0) forgotMutation.mutate(sentTo);
	};

	const identifierError = getFieldError(validationErrors, 'identifier');

	return (
		<>
			<CssBaseline enableColorScheme />
			<SignInContainer direction="column" justifyContent="center">
				<Card variant="outlined">
					<Typography component="h1" variant="h5" sx={{ textAlign: 'center' }}>
						Quên mật khẩu
					</Typography>

					{notice && <Alert severity={notice.severity}>{notice.text}</Alert>}

					{sentTo ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'center' }}>
							<Alert severity="success">
								Đã gửi mã xác nhận tới email của tài khoản. Mã hết hạn sau {OTP_EXPIRES_MINUTES} phút.
							</Alert>
							<Button
								variant="outlined"
								onClick={handleResend}
								disabled={cooldown > 0 || forgotMutation.isPending}
							>
								{forgotMutation.isPending
									? 'Đang gửi...'
									: cooldown > 0 ? `Gửi lại mã (${cooldown}s)` : 'Gửi lại mã'}
							</Button>
							<Link component={RouterLink} to={resetHref(sentTo)} variant="body2">
								Tôi đã có mã
							</Link>
							<Link component={RouterLink} to="/login" variant="body2">
								Quay lại đăng nhập
							</Link>
						</Box>
					) : (
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
							sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
						>
							<Typography variant="body2" color="text.secondary">
								Nhập tên đăng nhập hoặc email. Mã xác nhận sẽ được gửi tới email của tài khoản.
							</Typography>
							<FormControl>
								<FormLabel htmlFor="identifier">Tên đăng nhập/Email</FormLabel>
								<TextField
									id="identifier"
									name="identifier"
									autoComplete="username"
									autoFocus
									required
									fullWidth
									value={identifier}
									onChange={(e) => {
										setIdentifier(e.target.value);
										if (validationErrors) setValidationErrors(null);
									}}
									error={!!identifierError}
									helperText={identifierError}
								/>
							</FormControl>
							<Button type="submit" variant="contained" fullWidth disabled={forgotMutation.isPending}>
								{forgotMutation.isPending ? 'Đang gửi...' : 'Gửi mã xác nhận'}
							</Button>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Link component={RouterLink} to="/login" variant="body2">
									Quay lại đăng nhập
								</Link>
								<Link component={RouterLink} to={resetHref(identifier)} variant="body2">
									Tôi đã có mã
								</Link>
							</Box>
						</Box>
					)}
				</Card>
			</SignInContainer>
		</>
	);
}
